import {getBeginOfMonth, getWithAddedMonths, serializeDate} from "../../util/date-utils"
import {TransactionsWithCount} from "../../domain/transactions-with-count"
import {Transaction} from "../../domain/transaction"
import {Bank} from "../../components-features/dashboard/import/bank-format"
import {TransactionRange} from "../../domain/transaction-range"
import {throwOnErrorWithMessage} from "./api-utils"

export interface ITransactionsApi {
  getTransactions(transactionsSettings: TransactionsSettings, signal?: AbortSignal): Promise<TransactionsWithCount>
  getTransactionRange(signal?: AbortSignal): Promise<TransactionRange | null>
  updateTransaction(transaction: Transaction, signal?: AbortSignal): Promise<void>
  importTransactions(bank: Bank, file: File, signal?: AbortSignal): Promise<ImportTransactionsResponse>
}

export class TransactionsApi implements ITransactionsApi {
  constructor(private baseUrl: string) {
  }

  async getTransactions(transactionsSettings: TransactionsSettings, signal?: AbortSignal): Promise<TransactionsWithCount> {
    const parameters = []

    if (transactionsSettings.categoryId !== undefined) parameters.push(`categoryId=${transactionsSettings.categoryId}`)

    if (transactionsSettings.dateRange !== undefined) {
      const dates = getDates(transactionsSettings.dateRange)
      if (dates.from !== undefined) parameters.push(`from=${serializeDate(dates.from)}`)
      if (dates.until !== undefined) parameters.push(`until=${serializeDate(dates.until)}`)
    }

    if (transactionsSettings.sortOn === undefined) transactionsSettings.sortOn = {sort: "date", sortDesc: true}
    parameters.push(`sort=${transactionsSettings.sortOn.sort}`)
    parameters.push(`sortDesc=${transactionsSettings.sortOn.sortDesc}`)

    if (transactionsSettings.name !== "") parameters.push(`filterDescription=${transactionsSettings.name}`)

    if (transactionsSettings.unlimitedTransactions) parameters.push("unlimitedTransactions=true")

    const url = `${this.baseUrl}/transactions?${parameters.join("&")}`
    const response = await fetch(url, {method: "get", signal, credentials: "include"})
    return await response.json()
  }

  async getTransactionRange(signal?: AbortSignal): Promise<TransactionRange | null> {
    const url = `${this.baseUrl}/transactions/range`
    const response = await fetch(url, {method: "get", signal, credentials: "include"})
    await throwOnErrorWithMessage(response)
    const result = await response.json()
    return { first: new Date(result.first), last: new Date(result.last)}
  }

  async updateTransaction(transaction: Transaction, signal?: AbortSignal): Promise<void> {
    const url = `${this.baseUrl}/transactions/${transaction.id}`
    const response = await fetch(url, {method: "put", body: JSON.stringify(transaction), headers: {"Content-Type": "application/json"}, signal, credentials: "include"})
    if (!response.ok) throw new Error("Error occurred when updating transaction")
  }

  async importTransactions(bank: Bank, file: File, signal?: AbortSignal): Promise<ImportTransactionsResponse> {
    let formData = new FormData()
    formData.append("image", file)
    const url = `${this.baseUrl}/importtransactions?bank=${bank}`
    const response = await fetch(url, {method: "post", body: formData, signal, credentials: "include"})

    if (response.status === 200) {
      return {
        success: await response.json()
      }
    } else if (response.status === 400) {
      return {
        failure: await response.json()
      }
    } else {
      throw await response.text()
    }
  }
}

const getDates = (dateRange: DateRange): DateFromAndUntil => {
  let returnValue: DateFromAndUntil = {}
  if (dateRange !== undefined) {
    if (dateRange.type === "custom") {
      if (dateRange.from !== undefined) returnValue.from = dateRange.from
      if (dateRange.until !== undefined) returnValue.until = dateRange.until
    } else if (dateRange.type === "month") {
      if (dateRange.monthStart === undefined) throw new Error("monthStart should be given for a date range of type month")
      returnValue.from = dateRange.monthStart
      returnValue.until = getWithAddedMonths(dateRange.monthStart, 1)
    } else if (dateRange.type === "relative") {
      if (dateRange.relativeDateRange === "thisMonth") {
        returnValue.from = getBeginOfMonth(new Date())
        returnValue.until = getWithAddedMonths(returnValue.from, 1)
      } else if (dateRange.relativeDateRange === "fullYear") {
        returnValue.until = new Date()
        returnValue.from = new Date(returnValue.until.getFullYear() - 1, returnValue.until.getMonth(), returnValue.until.getDate())
      } else if (dateRange.relativeDateRange === "3FullMonths") {
        returnValue.until = new Date()
        returnValue.from = getWithAddedMonths(returnValue.until, -3)
      } else if (dateRange.relativeDateRange === "yearToDate") {
        returnValue.until = new Date()
        returnValue.from = new Date(returnValue.until.getFullYear(), 0, 1)
      } else if (dateRange.relativeDateRange === "lastYear") {
        returnValue.from = new Date(new Date().getFullYear() - 1, 0, 1)
        returnValue.until = new Date(returnValue.from.getFullYear() + 1, 0, 1)
      }
    }
  }
  return returnValue
}

interface DateFromAndUntil {
  from?: Date
  until?: Date
}

export interface TransactionsSettings {
  categoryId?: number
  name: string
  dateRange?: DateRange
  sortOn: SortOn
  unlimitedTransactions?: boolean
}

export const getInitialTransactionsSettings: () => TransactionsSettings = () => {
  return {
    name: "",
    sortOn: {sort: "amount", sortDesc: true}
  }
}

export type RelativeDateRange = "thisMonth" | "yearToDate" | "lastYear" | "fullYear" | "3FullMonths"

export interface DateRange {
  type: "relative" | "month" | "custom"
  relativeDateRange?: RelativeDateRange
  monthStart?: Date
  from?: Date
  until?: Date
}

export type SortOptions = "date" | "category" | "description" | "amount" | "impact"

export const defaultSortDesc: Record<SortOptions, boolean> = {
  "date": true,
  "category": false,
  "description": false,
  "amount": true,
  "impact": true
}

export interface SortOn {
  sort: SortOptions
  sortDesc: boolean
}

export interface ImportTransactionsResponse {
  success?: ImportTransactionsSuccessResponse
  failure?: ImportTransactionsFailureResponse
}

export interface ImportTransactionsSuccessResponse {
  amount: number
}

export interface ImportTransactionsFailureResponse {
  title: string
  Errors: FailedParseRowResult[]
}

export interface FailedParseRowResult {
  row: string[]
  FieldParseResults: FieldParseResult[]
}

export interface FieldParseResult {
  isSuccessful: boolean
  errorMessage: string
  importTransactionField: ImportTransactionField
}

export type ImportTransactionField = "Iban" | "SequenceNumber" | "Date" | "Amount" | "BalanceAfterTransaction" | "CounterpartyIban" | "CounterpartyName" | "Description" | "DebitCredit"
